import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";
import { useAuth } from "./auth";

/**
 * TenantContext — Fase 4 multi-tenant.
 *
 * Responsabilidades:
 * - Cargar `/api/tenants/current` para saber el tenant activo del usuario.
 * - Si es `super_admin`, cargar la lista de tenants y permitir cambiar el
 *   `activeTenantId` (guardado en localStorage). El axios interceptor lee este
 *   valor y adjunta `?tenant_id=xxx` en cada request cuando difiere del
 *   tenant nativo del usuario.
 * - Exponer `switch(tenantId)` para el selector.
 * - Exponer `refresh()` para recargar tras crear/editar tenants.
 */
const TenantCtx = createContext(null);

const ACTIVE_TENANT_KEY = "trf_active_tenant_id";

export function TenantProvider({ children }) {
    const { user } = useAuth();
    const [tenant, setTenant] = useState(null);       // tenant actualmente activo (mi tenant o impersonado)
    const [list, setList] = useState([]);              // lista de tenants (super_admin only)
    const [activeTenantId, setActiveTenantId] = useState(
        () => localStorage.getItem(ACTIVE_TENANT_KEY) || null,
    );
    const [loading, setLoading] = useState(false);

    const isSuperAdmin = Boolean(user?.super_admin);

    const fetchCurrent = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/tenants/current");
            setTenant(data);
        } catch {
            setTenant(null);
        }
    }, [user]);

    const fetchList = useCallback(async () => {
        if (!user || !isSuperAdmin) {
            setList([]);
            return;
        }
        try {
            const { data } = await api.get("/admin/tenants");
            setList(data);
        } catch {
            setList([]);
        }
    }, [user, isSuperAdmin]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([fetchCurrent(), fetchList()]);
        } finally {
            setLoading(false);
        }
    }, [fetchCurrent, fetchList]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const switchTenant = useCallback((tid) => {
        if (!tid || tid === user?.tenant_id) {
            localStorage.removeItem(ACTIVE_TENANT_KEY);
            setActiveTenantId(null);
        } else {
            localStorage.setItem(ACTIVE_TENANT_KEY, tid);
            setActiveTenantId(tid);
        }
        // Recargar tenant info (esto también triggerea re-fetch de páginas via query key)
        setTimeout(() => window.location.reload(), 50);
    }, [user]);

    const isImpersonating = useMemo(
        () => Boolean(activeTenantId) && activeTenantId !== user?.tenant_id,
        [activeTenantId, user],
    );

    const value = useMemo(
        () => ({
            tenant,
            list,
            loading,
            activeTenantId: activeTenantId || user?.tenant_id || null,
            isImpersonating,
            isSuperAdmin,
            switchTenant,
            refresh,
        }),
        [tenant, list, loading, activeTenantId, user, isImpersonating, isSuperAdmin,
         switchTenant, refresh],
    );

    return <TenantCtx.Provider value={value}>{children}</TenantCtx.Provider>;
}

export function useTenant() {
    const ctx = useContext(TenantCtx);
    if (!ctx) throw new Error("useTenant must be used within TenantProvider");
    return ctx;
}

export function getActiveTenantId() {
    return localStorage.getItem(ACTIVE_TENANT_KEY);
}
